import React, {
  useCallback,
  ChangeEvent,
} from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import MUICheckbox from '@mui/material/Checkbox';
import styled from '@emotion/styled';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import PropTypes, { InferProps } from 'prop-types';
import renderController from '../lib/renderController';
import overlay from '../Palettes/overlay';

const StyledFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin: 0 0 0 5px;
    display: inline;
  }

  .MuiFormControlLabel-label {
    color: ${overlay[600]};
    font-family: ${({ theme }) => theme.fonts.primary};
    transform: none;
    letter-spacing: unset;
    margin: 2px 0 0px;
  }

  .MuiButtonBase-root {
    padding: 3px;
    display: inline;
  }

  .MuiTypography-root {
    font-family: ${({ theme }) => theme.fonts.secondary};
    display: inline;
  }

  input {
    height: 150%;
    margin-top: -16px;
  }
`;

const checkboxPropertyTypes = {
  checked: PropTypes.bool,
  ControlLabel: PropTypes.elementType,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  id: PropTypes.string,
};

type CheckboxProperties = Omit<InferProps<
typeof checkboxPropertyTypes>, 'ControlLabel'> & {
  checked?: boolean;
  color?: 'default' | 'error' | 'info' | 'primary' | 'secondary' | 'success' | 'warning';
  ControlLabel?: React.ElementType | string;
  disabled?: boolean;
  indeterminate?: boolean;
  label?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
};

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProperties>(({
  ControlLabel,
  checked,
  onChange,
  label,
  indeterminate,
  disabled,
  color,
  id,
}: CheckboxProperties, reference) => {
  const ControlLabelComponent = ControlLabel ?? StyledFormControlLabel;

  return (
    <ControlLabelComponent
      control={(
        <MUICheckbox
          ref={reference}
          name={label}
          checked={checked ?? false}
          onChange={onChange}
          indeterminate={indeterminate ?? false}
          disabled={disabled ?? false}
          color={color ?? 'primary'}
          id={id}
        />
        )}
      label={label}
    />
  );
});

Checkbox.propTypes = checkboxPropertyTypes;

const checkboxWithControllerPropertyTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  rules: PropTypes.shape({}).isRequired,
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

type CheckboxWithControllerProperties<
  T extends FieldValues = FieldValues,
> = Omit<
  InferProps<typeof checkboxWithControllerPropertyTypes>,
  'control' | 'rules'
> & {
  control: Control<T>;
  rules?: UseControllerProps['rules'];
  ControlLabel?: React.ElementType | string;
};

export const CheckboxWithController = <T extends FieldValues = FieldValues>({
  name,
  control,
  rules,
  label,
  id,
  ControlLabel,
}: CheckboxWithControllerProperties<T>) => {
  const render = useCallback(
    ({
      field: { onChange, value, ref },
    }: {

      field: ControllerRenderProps<T, FieldPath<T>>;
    }) => (
      <Checkbox
        onChange={
          (
            { target: { checked } }: ChangeEvent<HTMLInputElement>,
          ) => onChange(checked)
        }
        checked={value}
        ref={ref}
        label={label}
        id={id}
        ControlLabel={ControlLabel}
      />
    ),
    [label, id, ControlLabel],
  );

  return renderController<T>({
    control, name, rules, render,
  });
};

CheckboxWithController.propTypes = checkboxWithControllerPropertyTypes;

export default Checkbox;
